import { useQuery, useMutation, useQueryClient } from 'react-query';
import { get, put, post, del } from '../utils/api';
import i18n from '../i18n';
import { track } from '../utils/track';

import { useErrorSnack, useSuccessSnack } from '../utils/snackbar';

async function getAssetDocument(assetId, assetType, documentId, isGuest) {
  const res = await get(
    `${isGuest ? '/guest' : ''}/assets/${assetId}/documents/${documentId}`,
    {
      params: { type: assetType },
    }
  );
  return res?.data;
}

async function getAssetDocuments(assetId, assetType, isGuest) {
  const res = await get(
    `${isGuest ? '/guest' : ''}/assets/${assetId}/documents`,
    {
      params: { type: assetType },
    }
  );
  return res?.data;
}

async function getDocument(documentId) {
  const res = await get(`/documents/${documentId}`);
  return res?.data;
}

async function updateDocument({ documentId, ...data }) {
  const res = await put(`/documents/${documentId}`, data);
  return res?.data;
}

async function updateTemplate({ meetingId, documentId, template }) {
  const res = await put(`/documents/${documentId}/template`, { template });
  return res?.data;
}

async function createDocument({
  assetId,
  assetType,
  template,
  title,
  status,
  folderId,
}) {
  const res = await post(`/documents`, {
    template,
    title,
    fileId: assetId,
    type: assetType,
    status,
    folderId,
  });
  return res?.data;
}

async function deleteDocument(documentId) {
  const res = await del(`/documents/${documentId}`);
  return res?.data;
}

export async function exportDocument(id, { format }) {
  track('export-document', {
    format,
    id,
  });
  const res = await get(`/documents/${id}/export`, {
    responseType: 'arraybuffer',
    params: {
      format,
    },
  });
  return res?.data;
}

export function useDocumentsQuery(assetId, assetType, isGuest) {
  const { data, ...rest } = useQuery(['documents', assetId, assetType], () =>
    getAssetDocuments(assetId, assetType, isGuest)
  );
  return {
    ...rest,
    docs: data?.docs,
  };
}

export function useDocumentQuery(
  assetId,
  assetType,
  documentId,
  isGuest,
  { enabled }
) {
  const { data: doc, ...rest } = useQuery(
    ['document', assetId, assetType, documentId],
    () => getAssetDocument(assetId, assetType, documentId, isGuest),
    {
      enabled,
    }
  );
  return {
    ...rest,
    doc,
  };
}

export function useDocumentByIdQuery(documentId) {
  return useQuery(['document', documentId], () => getDocument(documentId), {
    enabled: !!documentId,
  });
}

export function useDocumentActions(meetingId, documentId) {
  const queryClient = useQueryClient();

  const update = useMutation(
    (data) => updateDocument({ ...data, documentId }),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          ['document', data.assetId, data.assetType, documentId],
          data
        );
        queryClient.setQueryData(['document', documentId], data);
        queryClient.invalidateQueries(['folderAssets']);
      },
    }
  );

  useErrorSnack(i18n.t('Kunde inte uppdatera dokumentet'), update.isError);

  const updateTemplateMutation = useMutation(
    (template) => updateTemplate({ documentId, template }),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          ['document', data.assetId, data.assetType],
          data
        );
        queryClient.invalidateQueries(['document', data.assetId]);
      },
    }
  );

  useErrorSnack(
    i18n.t('Kunde inte uppdatera mallen för dokumentet'),
    updateTemplateMutation.isError
  );

  const exportDocumentMutation = useMutation((options) =>
    exportDocument(documentId, options)
  );

  useErrorSnack(
    i18n.t('Kunde inte exportera dokumentet'),
    exportDocumentMutation.isError
  );

  const _del = useMutation(() => deleteDocument(documentId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['document', documentId]);
      queryClient.invalidateQueries(['documents', documentId]);
      queryClient.invalidateQueries(['folderAssets']);
    },
  });

  useSuccessSnack(i18n.t('Dokument borttaget'), _del.isSuccess);
  useErrorSnack(i18n.t('Kunde inte ta bort dokumentet'), _del.isError);

  return {
    updateDocument: update.mutateAsync,
    updateDocumentTemplate: updateTemplateMutation.mutateAsync,
    exportDocument: exportDocumentMutation.mutateAsync,
    deleteDocumentAsync: _del.mutateAsync,
    isDeleting: _del.isLoading,
  };
}

export function useDocumentsActions(assetId) {
  const queryClient = useQueryClient();
  const create = useMutation((data) => createDocument({ ...data, assetId }), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['document', assetId]);
      queryClient.invalidateQueries(['documents', assetId]);
      queryClient.invalidateQueries('folderAssets');
    },
  });

  useSuccessSnack(i18n.t('Dokument skapat'), create.isSuccess);
  useErrorSnack(i18n.t('Kunde inte skapa dokumentet'), create.isError);

  const _del = useMutation((documentId) => deleteDocument(documentId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['document', assetId]);
      queryClient.invalidateQueries(['documents', assetId]);
    },
  });

  useSuccessSnack(i18n.t('Dokument borttaget'), _del.isSuccess);
  useErrorSnack(i18n.t('Kunde inte ta bort dokumentet'), _del.isError);

  return {
    createDocument: create.mutateAsync,
    deleteDocument: _del.mutateAsync,
  };
}
